import { useShowNotification } from '@inflearn/ds-react-next';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import {
  API_ENDPOINT,
  type ClientApiV1NotesIdMetadata,
  type ClientApiV1NotesIdMetadataResponse,
  useClientApiV1NotesPublishCreateMutation,
  useClientApiV1NotesUnpublishCreateMutation,
} from '../../resources/@types/uccSchema';
import { getUseNoteIdMetadataQueryKey } from '../components/pages/notes/hooks/queries/useNoteIdMetadataQuery';
import useRouterQueryId from '../hooks/useRouterQueryId';
import { setQueryDataUpdater } from '../utils/setQueryDataUpdater';

function useNotePublishMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const courseNoteId = useRouterQueryId();
  const { showNotification } = useShowNotification();

  const handleOnMutate = async () => {
    await queryClient.cancelQueries(getUseNoteIdMetadataQueryKey(courseNoteId));

    const previousMetadataResponse = queryClient.getQueryData<ClientApiV1NotesIdMetadataResponse>(
      getUseNoteIdMetadataQueryKey(courseNoteId),
    );

    const previousMetadata = previousMetadataResponse?.data;

    if (previousMetadata) {
      queryClient.setQueryData(
        getUseNoteIdMetadataQueryKey(courseNoteId),
        setQueryDataUpdater({
          ...previousMetadata,
          isPublic: !previousMetadata.isPublic,
        }),
      );
    }

    return { previousMetadata };
  };

  const handleOnError = (
    _error: unknown,
    _variables: unknown,
    context: { previousMetadata?: ClientApiV1NotesIdMetadata['data'] } | undefined,
  ) => {
    const previousMetadata = context?.previousMetadata;

    if (previousMetadata) {
      queryClient.setQueryData(
        getUseNoteIdMetadataQueryKey(courseNoteId),
        setQueryDataUpdater(previousMetadata),
      );
    }

    showNotification({
      type: 'error',
      title: t('노트 공개 실패'),
      message: t('잠시 후 다시 시도해 주세요.'),
    });
  };

  const options = {
    onMutate: handleOnMutate,
    onError: handleOnError,
  };

  const { mutate: publishMutate } = useClientApiV1NotesPublishCreateMutation({
    url: API_ENDPOINT.CLIENT_V1_NOTES_PUBLISH,
    options: options,
  });

  const { mutate: unpublishMutate } = useClientApiV1NotesUnpublishCreateMutation({
    url: API_ENDPOINT.CLIENT_V1_NOTES_UNPUBLISH,
    options: options,
  });

  const togglePublishMutate = ({ id, isPublic }: { id: number; isPublic: boolean }) => {
    const mutate = isPublic ? publishMutate : unpublishMutate;

    mutate({
      id: id,
    });
  };

  return {
    togglePublishMutate,
  };
}

export default useNotePublishMutation;
